import { Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useConfigStore } from "../../store/configStore";
import { useEditorStore } from "../../store/editorStore";
import useSWRMutation from "swr/mutation";
import useSWR from "swr";
import { putFetcher, postFetcher, formDataFetcher } from "api/swr";
import { toast } from "@/components/ui/use-toast";
import { useParams } from "react-router-dom";
import { useState } from "react";
// @ts-ignore - JSZip types may not be available
import JSZip from "jszip";
import { SaveProgressOverlay } from "../../components/SaveProgressOverlay";
import { SpineTemplate } from "../../types/spine-template";
import {
  buildExistingSelectionDocIdMap,
  sortSelectionsByPlayIndex,
} from "../../lib/selectionUtils";

// 计算字符串的 hash（使用 SHA-256）
async function computeStringHash(str: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(str);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

// 计算 Spine 源文件内容的 hash（不依赖 ZIP 打包）
async function computeSpineContentHash(spineData: any): Promise<string> {
  // 组合所有源文件内容
  const parts: string[] = [];
  
  // 骨骼数据（JSON 或 skel）
  if (spineData.skelBytes) {
    // .skel 二进制：用 base64 编码作为指纹内容
    const bytes = spineData.skelBytes as Uint8Array;
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    parts.push(btoa(binary));
  } else {
    parts.push(spineData.jsonText || "");
  }
  
  // Atlas 文件
  if (spineData.atlasTexts) {
    const sortedAtlasKeys = Object.keys(spineData.atlasTexts).sort();
    for (const key of sortedAtlasKeys) {
      parts.push(`${key}:${spineData.atlasTexts[key]}`);
    }
  }
  
  // PNG 文件
  if (spineData.pngDataUrls) {
    const sortedKeys = Object.keys(spineData.pngDataUrls).sort();
    for (const key of sortedKeys) {
      parts.push(`${key}:${spineData.pngDataUrls[key]}`);
    }
  }
  
  return computeStringHash(parts.join("|"));
}

export function SaveButton() {
  const { tid } = useParams<{ tid: string }>();
  const {
    templateConfig,
    spineData,
  } = useConfigStore();
  const {
    existingZipUrl,
    existingFingerprint,
    existingSpineVersion,
    spineZipDirty,
    isDirty,
    setIsDirty,
  } = useEditorStore();
  const [currentTask, setCurrentTask] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // 获取当前 template 数据（用于读取已有的 selectionIds）
  const { data: currentTemplate } = useSWR<SpineTemplate>(
    tid ? [`/spine-templates/${tid}`] : null
  );

  const { trigger: saveTemplate } = useSWRMutation(
    tid ? `/spine-templates/${tid}` : null,
    putFetcher,
    {
      onSuccess: () => {
        setIsSaving(false);
        setCurrentTask(null);
        setIsDirty(false); // 保存成功后重置脏状态
        toast({
          title: "保存成功",
          description: "配置已保存到数据库",
          className: "bg-green-500 text-white",
        });
      },
      onError: (error) => {
        setIsSaving(false);
        setCurrentTask(null);
        console.error("保存失败:", error);
        toast({
          variant: "destructive",
          title: "保存失败",
          description: error.message || "未知错误",
        });
      },
    }
  );

  const { trigger: uploadZipFile } = useSWRMutation(
    "/spine-templates/upload-zip",
    formDataFetcher
  );

  const { trigger: saveSelections } = useSWRMutation(
    "/spine-selections/batch",
    postFetcher
  );

  const { trigger: upsertTonings } = useSWRMutation(
    "/spine-tonings/upsert-many",
    postFetcher
  );

  const createZipFromSpineData = async (): Promise<Blob> => {
    if (!spineData || !tid) {
      throw new Error("缺少 Spine 数据");
    }

    const zip = new JSZip();

    // 添加骨骼数据文件（JSON 或 skel）
    if (spineData.skelBytes) {
      zip.file(`${tid}.skel`, spineData.skelBytes as Uint8Array);
    } else {
      zip.file(`${tid}.json`, spineData.jsonText);
    }

    // 添加所有 Atlas 文件
    if (spineData.atlasTexts) {
      const atlasTexts = spineData.atlasTexts as Record<string, string>;
      for (const [atlasFileName, atlasText] of Object.entries(atlasTexts)) {
        zip.file(atlasFileName, atlasText);
      }
    }

    // 添加所有 PNG 文件
    if (spineData.pngDataUrls) {
      const pngDataUrls = spineData.pngDataUrls as Record<string, string>;
      for (const [fileName, dataUrl] of Object.entries(pngDataUrls)) {
        const pngBlob = await fetch(dataUrl).then((r) => r.blob());
        zip.file(fileName, pngBlob);
      }
    }

    // 生成 zip 文件
    return await zip.generateAsync({ type: "blob" });
  };

  const handleSave = async () => {
    if (!templateConfig || !tid) {
      toast({
        variant: "destructive",
        title: "保存失败",
        description: "缺少配置数据或模板 ID",
      });
      return;
    }

    setIsSaving(true);

    try {
      let spineZipUrl: string | undefined;
      let spineFingerprint: string | undefined;
      let spineVersion: number | undefined;
      let spineCompressedZipUrl: string | undefined;

      // Zip 上传规则：
      // - Zip URL 内的 tid 可与当前模版 tid 不一致（子模版可共用父 Zip）
      // - 仅在本会话重新导入了 Spine 源文件（spineZipDirty）且内容确实变化时才上传
      // - 只改 Selection / 配置 / 信息 → 绝不重传 Zip
      if (!spineData) {
        spineZipUrl = existingZipUrl || undefined;
        spineFingerprint = existingFingerprint || undefined;
        spineVersion = existingSpineVersion || undefined;
        spineCompressedZipUrl =
          currentTemplate?.spineCompressedZipUrl || undefined;
      } else {
        setCurrentTask("正在检查内容变化...");
        const newHash = await computeSpineContentHash(spineData);
        const canReuseExistingZip = Boolean(existingZipUrl) && !spineZipDirty;
        const contentUnchanged =
          Boolean(existingFingerprint) && existingFingerprint === newHash;

        if (canReuseExistingZip || (existingZipUrl && contentUnchanged)) {
          setCurrentTask("复用已有 Zip，跳过上传...");
          spineZipUrl = existingZipUrl || undefined;
          spineFingerprint = existingFingerprint || newHash;
          spineVersion = existingSpineVersion || undefined;
          spineCompressedZipUrl =
            currentTemplate?.spineCompressedZipUrl || undefined;
        } else {
          setCurrentTask("正在创建 ZIP 文件...");
          const zipBlob = await createZipFromSpineData();

          setCurrentTask("正在上传 ZIP 文件...");
          const formData = new FormData();
          formData.append("zip", zipBlob, `${tid}.zip`);
          formData.append("id", tid);
          const uploadResult = await uploadZipFile(formData);
          spineZipUrl = uploadResult.url;
          spineFingerprint = newHash;
          spineVersion = uploadResult.version;
          spineCompressedZipUrl = uploadResult.compressedUrl || undefined;
        }
      }

      // 3. 保存 selections 到 spineSelections 表
      const existingIdBySelectionId = buildExistingSelectionDocIdMap(
        currentTemplate,
      );
      const sortedSelections = sortSelectionsByPlayIndex(
        templateConfig.selection || [],
      );

      const selectionsForLegacy = sortedSelections.map((selection: any) => ({
        id: existingIdBySelectionId.get(selection.id) || undefined,
        layerIconId: selection.layerIconId,
        // 只保存必要的字段，与 spineTemplates.selections 保持一致
        data: JSON.stringify({
          id: selection.id,
          playIndex: selection.playIndex,
          layerIconId: selection.layerIconId,
          gid: selection.gid,
          background: selection.background,
          name: selection.name,
          skuIds: selection.skuIds || [],
          hidden: selection.hidden || undefined,
          opt: typeof selection.opt === "boolean" ? selection.opt : undefined,
        }),
      }));

      let selectionIds: string[] = [];
      if (selectionsForLegacy.length > 0) {
        setCurrentTask("正在保存 spineSelections...");
        const result = await saveSelections({
          tid,
          selections: selectionsForLegacy,
        });
        selectionIds = result.ids || [];
      }

      // 4. 保存调色方案到全局 spineTonings 表
      const toningsData = templateConfig.tonings || [];
      if (toningsData.length > 0) {
        setCurrentTask("正在保存调色方案到全局库...");
        // 准备要同步到全局库的数据（去除 _isNew 标记）
        const toningsToUpsert = toningsData.map((t: any) => {
          const { _isNew, ...toningData } = t;
          return toningData;
        });
        await upsertTonings({ tonings: toningsToUpsert });
      }

      // 5. 保存配置到数据库（包含 zip URL）
      setCurrentTask("正在保存 spineTemplates...");
      // tonings 现在只存 ID 数组
      const toningIds = toningsData.map((t: any) => t.id);
      const tonings = JSON.stringify(toningIds);
      const animations = JSON.stringify(templateConfig.animations || []);
      const requireCategories = JSON.stringify(
        templateConfig.requireCategories || []
      );
      // selections 存 Selection 自身属性 + skuIds
      const selections = JSON.stringify(
        sortedSelections.map((sel: any) => ({
          id: sel.id,
          playIndex: sel.playIndex,
          layerIconId: sel.layerIconId,
          gid: sel.gid,
          background: sel.background,
          name: sel.name,
          skuIds: sel.skuIds || [],
          hidden: sel.hidden || undefined,
          opt: typeof sel.opt === "boolean" ? sel.opt : undefined,
        })),
      );
      // spineSkus 直接从 templateConfig.skus 获取完整 SKU 信息
      const spineSkus = templateConfig.skus || {};

      await saveTemplate({
        tonings,
        animations,
        requireCategories,
        selections,
        selectionIds: JSON.stringify(selectionIds),
        spineSkus: JSON.stringify(spineSkus),
        spineZipUrl,
        spineFingerprint,
        spineVersion,
        spineCompressedZipUrl,
      });

      // 更新 store 中的 ZIP 信息，避免下次保存重复上传
      const {
        setExistingZipUrl,
        setExistingFingerprint,
        setExistingSpineVersion,
        setSpineZipDirty,
      } = useEditorStore.getState();
      if (spineZipUrl) setExistingZipUrl(spineZipUrl);
      if (spineFingerprint) setExistingFingerprint(spineFingerprint);
      if (spineVersion) setExistingSpineVersion(spineVersion);
      setSpineZipDirty(false);
    } catch (error) {
      setIsSaving(false);
      setCurrentTask(null);
      toast({
        variant: "destructive",
        title: "保存失败",
        description: error instanceof Error ? error.message : "未知错误",
      });
    }
  };

  return (
    <>
      <SaveProgressOverlay isOpen={isSaving} currentTask={currentTask} />
      <Button
        onClick={handleSave}
        disabled={!templateConfig || !tid || isSaving}
        variant={isDirty ? "default" : "outline"}
        size="default"
        className={`gap-1 ${isDirty ? "bg-amber-600 hover:bg-amber-700" : ""}`}
      >
        <Save className="h-4 w-4" />
        {isSaving ? "保存中..." : isDirty ? "保存 *" : "保存"}
      </Button>
    </>
  );
}
